import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { formatApiError } from '../services/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { UsersThree, Plus, Trash, Pencil, Check, X as XIcon, FloppyDisk, Lightning, Key, ChartLineUp, Clock } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: `${API}/api`, withCredentials: true });

/**
 * Verwaltung der Mitarbeiter-Zugänge mit granularen Rechten.
 * - Presets als Schnellstart
 * - Checkbox-Baum gruppiert nach Modul
 */
export default function EmployeeAccessCard({ currentUserRole }) {
  const [catalog, setCatalog] = useState({ permissions: [], presets: {} });
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);   // user id or 'new'
  const [form, setForm] = useState({ email: '', password: '', name: '', permissions: new Set() });
  const [saving, setSaving] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);   // user object
  const [resetPw, setResetPw] = useState('');
  const [activityTarget, setActivityTarget] = useState(null);
  const [activityData, setActivityData] = useState(null);
  const [activityLoading, setActivityLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [c, u] = await Promise.all([
        api.get('/users/permissions/catalog'),
        api.get('/users'),
      ]);
      setCatalog(c.data);
      setUsers((u.data || []).filter(x => x.role === 'employee'));
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (currentUserRole === 'admin') load(); /* eslint-disable-next-line */ }, [currentUserRole]);

  const groups = React.useMemo(() => {
    const g = {};
    (catalog.permissions || []).forEach(p => { (g[p.group] = g[p.group] || []).push(p); });
    return g;
  }, [catalog.permissions]);

  const startNew = () => {
    setEditingId('new');
    setForm({ email: '', password: '', name: '', permissions: new Set() });
  };
  const startEdit = (u) => {
    setEditingId(u.id);
    setForm({ email: u.email, password: '', name: u.name || '', permissions: new Set(u.permissions || []) });
  };
  const cancel = () => { setEditingId(null); };

  const toggle = (key) => {
    setForm(f => {
      const next = new Set(f.permissions);
      if (next.has(key)) next.delete(key); else next.add(key);
      return { ...f, permissions: next };
    });
  };
  const toggleGroup = (groupKeys, allOn) => {
    setForm(f => {
      const next = new Set(f.permissions);
      groupKeys.forEach(k => allOn ? next.delete(k) : next.add(k));
      return { ...f, permissions: next };
    });
  };
  const applyPreset = (name) => {
    const p = catalog.presets[name];
    if (!p) return;
    setForm(f => ({ ...f, permissions: new Set(p.permissions) }));
    toast.success(`Preset "${p.label}" geladen`, { description: `${p.permissions.length} Rechte gesetzt` });
  };

  const save = async () => {
    setSaving(true);
    try {
      const perms = Array.from(form.permissions);
      if (editingId === 'new') {
        if (!form.email || !form.password) { toast.error('E-Mail + Passwort erforderlich'); setSaving(false); return; }
        if (form.password.length < 8) { toast.error('Passwort min. 8 Zeichen'); setSaving(false); return; }
        await api.post('/users/employee', { ...form, permissions: perms });
        toast.success('Mitarbeiter angelegt');
      } else {
        await api.put(`/users/${editingId}/permissions`, { permissions: perms });
        toast.success('Rechte aktualisiert');
      }
      setEditingId(null);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Mitarbeiter ${u.email} wirklich löschen?`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success('Entfernt'); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const doResetPassword = async () => {
    if (!resetPw || resetPw.length < 8) { toast.error('Passwort mind. 8 Zeichen'); return; }
    try {
      await api.post(`/users/${resetTarget.id}/reset-password`, { new_password: resetPw });
      toast.success('Passwort zurückgesetzt', { description: `Mitteilung an ${resetTarget.email} weitergeben` });
      setResetTarget(null); setResetPw('');
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const openActivity = async (u) => {
    setActivityTarget(u);
    setActivityLoading(true);
    try {
      const r = await api.get(`/user-activity/${u.id}`);
      setActivityData(r.data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setActivityLoading(false); }
  };

  if (currentUserRole !== 'admin') return null;

  return (
    <div className="card-swiss p-6" data-testid="employee-access-card">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-purple-100 rounded-sm flex items-center justify-center">
            <UsersThree weight="fill" size={20} className="text-purple-700"/>
          </div>
          <div>
            <h2 className="text-lg font-bold">Mitarbeiter-Zugänge</h2>
            <p className="text-sm text-[#525866]">Feingranulare Rechte per Modul & Aktion. Presets zum Schnellstart.</p>
          </div>
        </div>
        {!editingId && (
          <Button size="sm" className="btn-primary" onClick={startNew} data-testid="emp-new-btn">
            <Plus weight="bold" size={14} className="mr-1"/> Mitarbeiter anlegen
          </Button>
        )}
      </div>

      {loading ? <div className="h-16 skeleton"/> : (
        <>
          {/* Liste */}
          {!editingId && (
            users.length > 0 ? (
              <ul className="space-y-1">
                {users.map(u => (
                  <li key={u.id} className="flex items-center justify-between bg-[#F7F8FA] rounded px-3 py-2 text-sm" data-testid={`emp-row-${u.id}`}>
                    <div className="min-w-0">
                      <p className="font-semibold truncate">{u.name}</p>
                      <p className="text-xs text-[#525866] truncate">{u.email} · <span className="text-purple-700">{(u.permissions || []).length} Rechte</span></p>
                    </div>
                    <div className="flex gap-1">
                      <button onClick={() => openActivity(u)} className="p-1.5 text-purple-600 hover:bg-purple-50 rounded" title="Aktivität" data-testid={`emp-activity-${u.id}`}><ChartLineUp size={14} weight="bold"/></button>
                      <button onClick={() => { setResetTarget(u); setResetPw(''); }} className="p-1.5 text-amber-600 hover:bg-amber-50 rounded" title="Passwort zurücksetzen" data-testid={`emp-reset-${u.id}`}><Key size={14} weight="bold"/></button>
                      <button onClick={() => startEdit(u)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded" data-testid={`emp-edit-${u.id}`}><Pencil size={14} weight="bold"/></button>
                      <button onClick={() => remove(u)} className="p-1.5 text-red-600 hover:bg-red-50 rounded" data-testid={`emp-delete-${u.id}`}><Trash size={14} weight="bold"/></button>
                    </div>
                  </li>
                ))}
              </ul>
            ) : <p className="text-sm text-[#8A94A6]">Noch kein Mitarbeiter angelegt.</p>
          )}

          {/* Edit/New Form */}
          {editingId && (
            <div className="space-y-4">
              {/* Base fields */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <div>
                  <Label className="overline">Name</Label>
                  <Input value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} className="input-swiss" data-testid="emp-form-name"/>
                </div>
                <div>
                  <Label className="overline">E-Mail *</Label>
                  <Input type="email" disabled={editingId !== 'new'} value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} className="input-swiss" data-testid="emp-form-email"/>
                </div>
                {editingId === 'new' && (
                  <div>
                    <Label className="overline">Passwort * (min 8)</Label>
                    <Input type="password" value={form.password} onChange={e => setForm(f => ({...f, password: e.target.value}))} className="input-swiss" data-testid="emp-form-password"/>
                  </div>
                )}
              </div>

              {/* Presets */}
              <div className="border-t border-[#E2E4E9] pt-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-semibold text-[#525866]"><Lightning size={12} weight="fill" className="inline mr-0.5 text-amber-500"/>Preset laden:</span>
                  {Object.entries(catalog.presets).map(([key, p]) => (
                    <button key={key} onClick={() => applyPreset(key)} className="text-xs px-2 py-1 bg-[#F7F8FA] hover:bg-gray-200 rounded" data-testid={`preset-${key}`}>
                      {p.label} <span className="text-[#8A94A6]">({p.permissions.length})</span>
                    </button>
                  ))}
                </div>
                <p className="text-xs text-[#8A94A6] mt-1">{form.permissions.size} von {catalog.permissions.length} Rechten aktiviert</p>
              </div>

              {/* Permission tree */}
              <div className="border border-[#E2E4E9] rounded p-3 max-h-[500px] overflow-y-auto" data-testid="emp-permissions">
                {Object.entries(groups).map(([groupName, items]) => {
                  const keys = items.map(i => i.key);
                  const allOn = keys.every(k => form.permissions.has(k));
                  const someOn = keys.some(k => form.permissions.has(k));
                  return (
                    <div key={groupName} className="mb-3 last:mb-0">
                      <button onClick={() => toggleGroup(keys, allOn)}
                              className={`text-sm font-bold mb-1 ${allOn ? 'text-emerald-700' : someOn ? 'text-amber-700' : 'text-[#111418]'}`}
                              data-testid={`group-${groupName}`}
                      >
                        {allOn ? '✓ ' : someOn ? '◐ ' : '☐ '}{groupName}
                      </button>
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-1 pl-4">
                        {items.map(p => (
                          <label key={p.key} className="flex items-start gap-2 text-xs cursor-pointer hover:bg-[#F7F8FA] rounded px-1 py-0.5" data-testid={`perm-${p.key}`}>
                            <input type="checkbox" className="mt-0.5 accent-[#E60000]"
                                   checked={form.permissions.has(p.key)}
                                   onChange={() => toggle(p.key)}/>
                            <span>
                              <span className="font-medium">{p.label}</span>
                              {p.description && <span className="text-[#8A94A6] block text-[10px]">{p.description}</span>}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={cancel}><XIcon size={14} className="mr-1"/> Abbrechen</Button>
                <Button className="btn-primary" onClick={save} disabled={saving} data-testid="emp-save-btn">
                  <FloppyDisk weight="bold" size={14} className="mr-1"/> {saving ? 'Speichere…' : (editingId === 'new' ? 'Anlegen' : 'Speichern')}
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Passwort-Reset Modal */}
      {resetTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" data-testid="reset-password-modal">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between px-5 py-3 border-b border-[#E2E4E9]">
              <div className="flex items-center gap-2">
                <Key weight="bold" size={18} className="text-amber-600"/>
                <h3 className="font-bold">Passwort zurücksetzen</h3>
              </div>
              <button onClick={() => setResetTarget(null)} className="p-1 hover:bg-gray-100 rounded"><XIcon size={18}/></button>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-sm text-[#525866]">Neues Passwort für <b>{resetTarget.email}</b> setzen. Der Mitarbeiter kann sich sofort damit anmelden.</p>
              <Input type="password" value={resetPw} onChange={e => setResetPw(e.target.value)} placeholder="Neues Passwort (min 8)" data-testid="reset-pw-input"/>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setResetTarget(null)}>Abbrechen</Button>
                <Button className="btn-primary" onClick={doResetPassword} data-testid="reset-pw-save"><Key weight="bold" size={14} className="mr-1"/>Passwort setzen</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Activity Modal */}
      {activityTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" data-testid="activity-modal">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-3 border-b border-[#E2E4E9]">
              <div className="flex items-center gap-2">
                <ChartLineUp weight="bold" size={18} className="text-purple-600"/>
                <div>
                  <h3 className="font-bold">Aktivitätsprotokoll</h3>
                  <p className="text-xs text-[#525866]">{activityTarget.name} · {activityTarget.email}</p>
                </div>
              </div>
              <button onClick={() => { setActivityTarget(null); setActivityData(null); }} className="p-1 hover:bg-gray-100 rounded"><XIcon size={18}/></button>
            </div>
            <div className="flex-1 overflow-auto p-5">
              {activityLoading ? <div className="h-24 skeleton"/> : !activityData || activityData.total === 0 ? (
                <p className="text-sm text-[#8A94A6] text-center py-12">Keine Aktivitäten protokolliert.</p>
              ) : (
                <>
                  <div className="flex flex-wrap gap-2 mb-3">
                    <div className="bg-purple-50 text-purple-800 rounded px-2 py-1 text-xs font-semibold">{activityData.total} Einträge</div>
                    {Object.entries(activityData.action_counts).map(([a, n]) => (
                      <div key={a} className="bg-[#F7F8FA] text-[#525866] rounded px-2 py-1 text-xs">{a}: <b>{n}</b></div>
                    ))}
                  </div>
                  <ul className="space-y-1.5">
                    {activityData.entries.map((e, i) => (
                      <li key={i} className="border-l-2 border-purple-300 pl-3 py-1 text-xs" data-testid={`activity-entry-${i}`}>
                        <div className="flex items-baseline justify-between gap-2">
                          <span className="font-semibold text-[#111418]">{e.entity_type} · {e.action}</span>
                          <span className="text-[#8A94A6] flex items-center gap-1"><Clock size={10}/>{e.created_at ? new Date(e.created_at).toLocaleString('de-DE') : ''}</span>
                        </div>
                        {e.entity_id && <p className="text-[#525866] font-mono text-[10px]">ID: {e.entity_id}</p>}
                        {e.note && <p className="text-[#111418] mt-0.5">{e.note}</p>}
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
